// Per-event drafts (Phase 2 multi-event): what an operator typed in a drawer stays with that event's tab
// when they switch to another event and back. Keys are `${eventId}:${name}`.

import { useEffect, useState, type Dispatch, type SetStateAction } from 'react';

export class DraftStore {
  private m = new Map<string, unknown>();
  has(key: string): boolean {
    return this.m.has(key);
  }
  get(key: string): unknown {
    return this.m.get(key);
  }
  set(key: string, v: unknown): void {
    this.m.set(key, v);
  }
  /** Drops one event's drafts, or only those whose name starts with `prefix`. */
  clear(eventId: string, prefix = ''): void {
    const p = `${eventId}:${prefix}`;
    for (const k of [...this.m.keys()]) if (k.startsWith(p)) this.m.delete(k);
  }
}

/** useState that starts from, and writes back to, the event's draft. */
export function useDraft<T>(store: DraftStore, eventId: string, name: string, init: T | (() => T)): [T, Dispatch<SetStateAction<T>>] {
  const key = `${eventId}:${name}`;
  const [v, setV] = useState<T>(() => (store.has(key) ? (store.get(key) as T) : typeof init === 'function' ? (init as () => T)() : init));
  useEffect(() => {
    store.set(key, v);
  }, [store, key, v]);
  return [v, setV];
}
